import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { AffiliateProgramService } from '@/services/affiliate-program.service';
import { AxiosError } from 'axios';
import { AxiosErrorResponse } from '@/@types/Errors';
import useModalStore from '@/store/useModalStore';
import { useTranslation } from 'react-i18next';
import { RequestedOnMarketplaceEnum } from '@/@types/Enums/marketplace';

interface ConfigureAffiliateProgramData {
  productId: string;
  contact_email: string;
  attribution: string;
  cookie_duration: number;
  commission: number;
  enable_affiliate_program: boolean;
  manual_affiliation_approval: boolean;
  access_buyer_contact_info: boolean;
  commission_rule_type?: string;
  commission_value?: number;
  currency?: string;
  description?: string;
  requested_on_marketplace?: RequestedOnMarketplaceEnum;
  pre_cbk_responsible?: string;
  page_url?: string;
}

export const useConfigureAffiliateProgram = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { closeModal } = useModalStore()
  const { t } = useTranslation("affiliates");

  const { mutate, isPending: isConfiguring } = useMutation({
    mutationFn: (data: ConfigureAffiliateProgramData) =>
      AffiliateProgramService.configureAffiliateProgram(data),
    onSuccess: async () => {
      toast({
        variant: 'success',
        description: t('toast.affiliate_program_configured_success'),
      });
      await queryClient.invalidateQueries({ queryKey: ['affiliate-program'] });
      closeModal("config-affiliate")
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      toast({
        variant: 'error',
        description:
          error?.response?.data?.message || t('toast.generic_error'),
      });
    },
  });

  return {
    configureAffiliateProgram: mutate,
    isConfiguring,
  };
};